// The moves behind Needs you, shared by the desktop list (actions inline) and the phone (a compact row that opens a
// sheet with the same actions). From the assistant (cards waiting for you, questions to answer), yours (your
// activities at risk or not started and due soon, your prep for a meeting soon), and gone quiet (no update for a
// week). Each resolves in place with what happened and, where it can be undone, Undo; resolved ones go last.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isSuggestion, PLACE_FIRST, type ProposedAction, type SessionUser, type TeamCheck, type TodayQuiet, type TodayYours } from '@dpw/shared';
import { api } from '../../api';
import { useDecide } from '../../lib/decide';
import { NOT_IGNORABLE } from '../../ui/ApproveCard';
import { IconAsk, IconBell, IconBook, IconCalendar, IconClip, IconClock, IconMail, IconRec } from '../../ui/icons';
import { useToast } from '../../ui/toast';
import { toIsoAt } from '../../lib/format';
import { callTime } from './util';

/** One thing a move lets you do: a button (run) or a way through (to). */
export interface Act { label: string; style: 'pri' | 'sec' | 'ghost'; run?: () => void; to?: string; disabled?: boolean }
export type Tone = 'grn' | 'acc' | 'amb' | 'red';
export interface Move {
  key: string; kind?: string; ai: boolean;
  /** The label ("Approval", "Question", "Prep", "At risk") and its desktop class. */
  lbl: [string, string]; icon: ReactNode; tone: Tone; title: string; why: string;
  /** What it is linked to (a space, track or activity); no chip when it is linked to nothing. */
  ctx: { label: string; to: string } | null;
  acts: Act[];
  /** A question to answer: the answer box takes the place of buttons. */
  answer?: { id: string; question: string };
}
export interface Done { result: string; undo?: () => void }
export interface QuietRow { key: string; q: TodayQuiet; to: string; who: string | null; done?: Done; nudge?: () => void; fine: () => void }

/** A type icon per row, in its tinted square: what kind of thing it is. */
function iconFor(a: ProposedAction): ReactNode {
  if (a.source === 'email' || a.kind === 'send_message') return <IconMail />;
  if (a.kind === 'confirm_answer' || a.kind === 'review_answer' || a.kind === 'nudge_check' || a.kind === 'check_in') return <IconAsk />;
  if (a.kind === 'file_minutes' || a.source === 'recording' || a.source === 'notetaker') return <IconRec />;
  if (a.kind === 'update_context') return <IconBook />;
  if (a.kind === 'create_reminder') return <IconBell />;
  if (a.kind === 'add_file') return <IconClip />;
  if (a.source === 'gone_quiet') return <IconClock />;
  return <IconCalendar />;
}

/**
 * What a card is linked to: its activity (when the card names it), else its track, else its space; none when it is
 * linked to nothing. A card about an activity it does not name links to the space it names, never under a wrong name.
 */
function linkOf(a: ProposedAction): Move['ctx'] {
  const p = a.payload as { activityId?: string; trackId?: string };
  const row = (label: string) => a.rows.find(r => r.label === label)?.value;
  const activity = row('Activity') ?? row('Linked to') ?? row('From an update on') ?? row('From the minutes of');
  if (p.activityId && activity) return { label: activity, to: `/activities/${p.activityId}` };
  if (p.trackId) return { label: row('Space and track') ?? ([a.spaceName, row('Track')].filter(Boolean).join(' · ') || 'Track'), to: `/tracks/${p.trackId}` };
  if (a.spaceId) return { label: a.spaceName ?? 'Space', to: `/spaces/${a.spaceId}` };
  return null;
}
export const first = (n: string) => n.split(' ')[0]!;

export function useMoves({ me, actions, checks, yours, quiet, onCount }: {
  me: SessionUser; actions: ProposedAction[]; checks: TeamCheck[]; yours: TodayYours[]; quiet: TodayQuiet[];
  onCount?: (open: number, cleared: number) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const decide = useDecide();
  const [done, setDone] = useState<Record<string, Done>>({});
  // What a row looked like, so it stays (resolved) after its source list no longer has it.
  const kept = useRef(new Map<string, Move>());
  const keptQuiet = useRef(new Map<string, TodayQuiet>());
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const resolve = (key: string, d: Done) => setDone(x => ({ ...x, [key]: d }));
  const unresolve = (key: string) => setDone(x => { const { [key]: _gone, ...rest } = x; return rest; });
  const refresh = () => void qc.invalidateQueries();
  const fail = (key: string) => (e: unknown) => { unresolve(key); toast(e instanceof Error ? e.message : 'That did not work.'); };

  const nudge = (key: string, about: { activityId?: string; trackId?: string }, title: string) => {
    resolve(key, { result: 'Sending a check-in' });
    api.checks.create({ question: `Any update on ${title}?`, ...about, audience: 'owners', expiresInHours: 48 }).then(c => {
      const to = c.view === 'full' ? c.recipients.map(r => first(r.name)).join(' and ') : 'the owners';
      resolve(key, { result: `Check-in sent to ${to}` });
      refresh();
    }, fail(key));
  };
  const sendAnswer = (m: Move) => {
    if (!m.answer) return;
    const v = (answers[m.answer.id] ?? '').trim();
    if (!v) return;
    const c = checks.find(x => x.id === m.answer!.id);
    resolve(m.key, { result: `Answer sent. ${c ? first(c.askedBy.name) : 'They'} will see it.` });
    api.checks.answer(m.answer.id, v).then(refresh, fail(m.key));
  };

  const ai: Move[] = [
    ...actions.filter(a => a.status === 'waiting' && !isSuggestion({ kind: a.kind, source: a.source })).map((a): Move => {
      const key = `a:${a.id}`;
      const row = a.rows[0];
      const review = `/requests?id=${a.id}`;
      return {
        key, kind: a.kind, ai: true, lbl: NOT_IGNORABLE.has(a.kind) ? ['Confirm', 'lbl q'] : PLACE_FIRST.has(a.kind) ? ['Not logged', 'lbl appr'] : ['Approval', 'lbl appr'], icon: iconFor(a), tone: 'grn', title: a.title,
        why: `${a.sourceLabel}${row ? `. ${row.label}: ${row.value}` : ''}`,
        ctx: linkOf(a),
        acts: NOT_IGNORABLE.has(a.kind)
          ? [{ label: a.kind === 'confirm_answer' ? 'Choose the right answer' : 'Review the answer', style: 'pri', to: review }]
          // An email the assistant could not place: Log it opens it in Requests to pick the space and track.
          : PLACE_FIRST.has(a.kind) ? [
            { label: 'Log it', style: 'pri', to: review },
            { label: 'Dismiss', style: 'sec', disabled: decide.busy(a.id), run: () => {
              resolve(key, { result: 'Dismissed. Nothing was logged.', undo: () => { unresolve(key); void api.actions.restore(a.id).then(refresh); } });
              decide.ignore(a);
            } },
          ]
          : [
            { label: 'Approve', style: 'pri', disabled: decide.busy(a.id), run: () => { resolve(key, { result: 'Approved' }); decide.approve(a); } },
            { label: 'Ignore', style: 'sec', disabled: decide.busy(a.id), run: () => {
              resolve(key, { result: 'Ignored. Nothing was done.', undo: () => { unresolve(key); void api.actions.restore(a.id).then(refresh); } });
              decide.ignore(a);
            } },
            { label: 'Review', style: 'ghost', to: review },
          ],
      };
    }),
    ...checks.map((c): Move => ({
      key: `c:${c.id}`, ai: true, lbl: ['Question', 'lbl q'], icon: <IconAsk />, tone: 'acc', title: c.question,
      why: `${c.askedBy.id === me.id ? 'You asked' : `${first(c.askedBy.name)} asked`}. Your answer finishes it.`,
      ctx: c.about ? { label: c.about.title, to: c.about.kind === 'activity' ? `/activities/${c.about.id}` : c.about.kind === 'track' ? `/tracks/${c.about.id}` : `/spaces/${c.about.id}` } : null,
      acts: [], answer: { id: c.id, question: c.question },
    })),
  ];
  const mine: Move[] = yours.map((y): Move => {
    const key = `y:${y.stepId ?? y.activityId}`;
    const ctx = { label: `${y.spaceName} · ${y.trackName}`, to: `/tracks/${y.trackId}` };
    const open = { label: 'Open', style: 'sec' as const, to: `/activities/${y.activityId}` };
    if (y.kind === 'prep') {
      return {
        key, ai: false, lbl: ['Prep', 'lbl prep'], icon: <IconClock />, tone: 'amb', title: y.title, why: y.why, ctx,
        acts: [
          { label: 'Mark ready', style: 'pri', run: () => {
            resolve(key, { result: 'Marked ready', undo: () => { unresolve(key); void api.activities.updateStep(y.stepId!, { done: false }).then(refresh); } });
            api.activities.updateStep(y.stepId!, { done: true }).then(refresh, fail(key));
          } },
          open,
        ],
      };
    }
    const who = y.other ? first(y.other.name) : null;
    return {
      key, ai: false, lbl: ['At risk', 'lbl risk'], icon: <IconBell />, tone: 'red', title: y.title, why: y.why, ctx,
      acts: who ? [
        { label: `Nudge ${who}`, style: 'pri', run: () => nudge(key, { activityId: y.activityId }, y.title) },
        { label: `I will call ${who}`, style: 'sec', run: () => {
          const { day, at, label } = callTime(new Date());
          resolve(key, { result: `Reminder set: call ${who}, ${label}` });
          api.reminders.create({ text: `Call ${who} about ${y.title}`, remindAt: toIsoAt(day, at), activityId: y.activityId }).then(r => {
            resolve(key, { result: `Reminder set: call ${who}, ${label}`, undo: () => { unresolve(key); void api.reminders.remove(r.id).then(refresh); } });
            refresh();
          }, fail(key));
        } },
      ] : [{ ...open, style: 'pri' }],
    };
  });

  for (const m of [...ai, ...mine]) kept.current.set(m.key, m);
  const shown = (list: Move[], prefix: (k: string) => boolean) => {
    const live = new Set(list.map(m => m.key));
    const gone = [...kept.current.values()].filter(m => prefix(m.key) && !live.has(m.key) && done[m.key]);
    const all = [...list, ...gone];
    return [...all.filter(m => !done[m.key]), ...all.filter(m => done[m.key])];
  };
  const aiShown = shown(ai, k => k.startsWith('a:') || k.startsWith('c:'));
  const mineShown = shown(mine, k => k.startsWith('y:'));
  // Gone quiet rows stay (resolved) after "It is fine" takes them out of the data.
  for (const q of quiet) keptQuiet.current.set(`q:${q.kind}:${q.id}`, q);
  const quietLive = new Set(quiet.map(q => `q:${q.kind}:${q.id}`));
  const quietRows: QuietRow[] = [...quiet, ...[...keptQuiet.current.entries()].filter(([k]) => !quietLive.has(k) && done[k]).map(([, q]) => q)].map(q => {
    const key = `q:${q.kind}:${q.id}`;
    const who = q.owner && q.owner.id !== me.id ? first(q.owner.name) : null;
    return {
      key, q, who, done: done[key], to: q.kind === 'activity' ? `/activities/${q.id}` : `/tracks/${q.id}`,
      nudge: who ? () => nudge(key, q.kind === 'activity' ? { activityId: q.id } : { trackId: q.id }, q.title) : undefined,
      fine: () => {
        resolve(key, { result: 'Hidden for 7 days', undo: () => { unresolve(key); void api.today.unhide(q.kind, q.id).then(refresh); } });
        api.today.hide(q.kind, q.id).catch(fail(key));
      },
    };
  });
  const open = [...aiShown, ...mineShown].filter(m => !done[m.key]).length;
  const cleared = Object.keys(done).filter(k => !k.startsWith('q:')).length;
  useEffect(() => onCount?.(open, cleared), [open, cleared, onCount]);

  return {
    aiShown, mineShown, quietRows, done, open, cleared,
    answerOf: (m: Move) => (m.answer ? answers[m.answer.id] ?? '' : ''),
    setAnswer: (m: Move, v: string) => { if (m.answer) setAnswers(x => ({ ...x, [m.answer!.id]: v })); },
    sendAnswer,
  };
}
